import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AddMemberPayload,
  CreateClientPayload,
  UpdateClientPayload,
  addMember,
  createClient,
  deleteClient,
  getClient,
  listMembers,
  listClients,
  removeMember,
  updateClient,
  uploadClientAvatar,
} from './api'

export function useClients() {
  return useQuery({
    queryKey: ['clients'],
    queryFn: listClients,
  })
}

export function useClient(clientId: string | null) {
  return useQuery({
    queryKey: ['clients', clientId],
    queryFn: () => getClient(clientId!),
    enabled: !!clientId,
  })
}

export function useCreateClient() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateClientPayload) => createClient(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] })
    },
  })
}

export function useUpdateClient(clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateClientPayload) =>
      updateClient(clientId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] })
      qc.invalidateQueries({ queryKey: ['clients', clientId] })
    },
  })
}

export function useUploadClientAvatar(clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => uploadClientAvatar(clientId, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] })
      qc.invalidateQueries({ queryKey: ['clients', clientId] })
    },
  })
}

export function useDeleteClient() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (clientId: string) => deleteClient(clientId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] })
    },
  })
}

export function useMembers(clientId: string | null) {
  return useQuery({
    queryKey: ['clients', clientId, 'members'],
    queryFn: () => listMembers(clientId!),
    enabled: !!clientId,
  })
}

export function useAddMember(clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: AddMemberPayload) => addMember(clientId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients', clientId, 'members'] })
    },
  })
}

export function useRemoveMember(clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => removeMember(clientId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients', clientId, 'members'] })
    },
  })
}
